import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import { createModal } from '@codegouvfr/react-dsfr/Modal';
import Stepper from '@codegouvfr/react-dsfr/Stepper';
import clsx from 'clsx';
import {
  isCreatedPartialSample,
  type PartialSample,
  type PartialSampleToCreate,
  type SampleChecked
} from 'maestro-shared/schema/Sample/Sample';
import { SampleSteps } from 'maestro-shared/schema/Sample/SampleStep';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import ContextStep from 'src/views/SampleView/DraftSample/ContextStep/ContextStep';
import ItemsStep from 'src/views/SampleView/DraftSample/ItemsStep/ItemsStep';
import MatrixStep from 'src/views/SampleView/DraftSample/MatrixStep/MatrixStep';
import SendingStep from 'src/views/SampleView/DraftSample/SendingStep/SendingStep';
import { SampleStepTitles } from 'src/views/SampleView/SampleView';
import audit from '../../../assets/illustrations/audit.svg';
import { usePartialSample } from '../../../hooks/usePartialSample';
import { useSamplesLink } from '../../../hooks/useSamplesLink';

import '../SampleView.scss';

const editConfirmModal = createModal({
  id: 'sample-edit-confirm-modal',
  isOpenedByDefault: false
});

interface Props {
  sample?: PartialSample | PartialSampleToCreate;
}

const SampleView = ({ sample }: Props) => {
  useDocumentTitle("Saisie d'un prélèvement");

  const { programmingPlan, canEdit, readonly } = usePartialSample(sample);
  const { getSampleStepParam } = useSamplesLink();

  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<number>();
  const isEditing = searchParams.get('isEditing') === 'true';

  useEffect(() => {
    if (sample) {
      setStep(getSampleStepParam() ?? SampleSteps[sample.step]);
    } else {
      setStep(1);
    }
  }, [sample, searchParams]);

  if (!programmingPlan) {
    return null;
  }

  return (
    <>
      <section className={clsx(cx('fr-container'), 'main-section')}>
        {canEdit && !isEditing && (
          <div className={clsx('align-right')}>
            <Button
              iconId="fr-icon-edit-line"
              priority="secondary"
              onClick={editConfirmModal.open}
            >
              Modifier le prélèvement
            </Button>
          </div>
        )}
        <div
          className={clsx(
            cx('fr-pt-3w', 'fr-pt-md-4w', 'fr-pb-6w'),
            'white-container'
          )}
        >
          {!readonly && step && (
            <div className="app-stepper">
              <img
                src={audit}
                height="100%"
                aria-hidden
                className={cx('fr-hidden', 'fr-unhidden-md')}
                alt=""
              />
              <Stepper
                currentStep={step}
                nextTitle={SampleStepTitles(sample)[step]}
                stepCount={4}
                title={
                  <div>
                    {SampleStepTitles(sample)[step - 1]}
                    {sample && isCreatedPartialSample(sample) && (
                      <span className={cx('fr-text--regular')}>
                        {' '}
                        • Prélèvement {sample.reference}
                      </span>
                    )}
                  </div>
                }
              />
            </div>
          )}
          {step === 1 && <ContextStep partialSample={sample} />}
          {step === 2 && sample && <MatrixStep partialSample={sample} />}
          {step === 3 && sample && <ItemsStep partialSample={sample} />}
          {step === 4 && sample && (
            <SendingStep sample={sample as SampleChecked} />
          )}
        </div>
      </section>
      <editConfirmModal.Component
        title="Modifier le prélèvement"
        buttons={[
          {
            children: 'Annuler',
            priority: 'secondary'
          },
          {
            children: 'Confirmer',
            onClick: () =>
              setSearchParams(
                (prev) => {
                  prev.set('isEditing', 'true');
                  return prev;
                },
                { replace: true }
              ),
            doClosesModal: true
          }
        ]}
      >
        Vous êtes sur le point de modifier un prélèvement dont vous n'êtes pas
        le préleveur. Souhaitez-vous continuer ?
      </editConfirmModal.Component>
    </>
  );
};

export default SampleView;
